import React, { useEffect, useRef, useState } from "react";
import "./style.scss";
import { useDispatch } from "react-redux";
import { fetchCommonData, getOutletsData, syncSingleOutletMenu, saveAllOutletsStatus } from "../../../redux/slices";
import { useSelector } from "react-redux";
import OutletListItem from "../outlet-list-item";
import ActivityLogModal from "../activity-log-modal";
import OutletStatusToggleModal from "../outlet-status-toggle-modal";
import { Box, Checkbox, Modal } from "@mui/material";
import CtaComponent from "../../../components/reusable-ui/cta";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { cloneDeep, get, unset } from "lodash";
import OutletOptionsMenu from "../outlet-more-options-menu";
import RenderSelectedOption from "../render-selected-outlet-option";
import emptyListPlaceholder from "../../../assets/images/empty-list-placeholder.svg";
import { METHODS } from "../../../data/api-method-constants";
import { ENDPOINT } from "../../../services/apiUrls";
import { OUTLET_STATUSES } from "../../../data/outlet-status-constants";
import moment from "moment";
import { getLocalStorage, setLocalStorage } from "../../../utils";
import { BRANCH_OUTLET_STATUS } from "../../../data/local-storage-constants";

interface Props {
  selectedBranches: any;
  searchData: any;
  outletGroupByBranchMap: any;
  addAllOutletsForBranch: any;
  bulkActionConfig: any;
  setBulkActionConfig: any;
  setApiStateModalData: any;
  updatedOutletsConfig: any;
  getBranchOutletsCount: any;
  getSearchResults: any;
  statusLoading: boolean;
}

const OutletViewComponent = (props: Props) => {
  const {
    selectedBranches,
    searchData,
    outletGroupByBranchMap,
    addAllOutletsForBranch,
    bulkActionConfig,
    setBulkActionConfig,
    setApiStateModalData,
    updatedOutletsConfig,
    getBranchOutletsCount,
    getSearchResults,
    statusLoading,
  } = props;
  const COOKIE_DURATION = 15;
  const [activityLogModalData, setActivityLogModalData] = useState<any>({
      show: false,
      outletId: "",
      channelDefName: "",
    }),
    [outletToggleModalData, setOutletToggleModalData] = useState<any>({
      show: false,
      outletData: {},
    }),
    [menuSyncModalData, setMenuSyncModalData] = useState<any>({
      show: false,
      outletData: {},
    }),
    [moreOptionsMenuData, setMoreOptionsMenuData] = useState({
      selectedOptionId: "",
      outletData: {},
      anchorEl: null,
    });
  let observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observer.current = new IntersectionObserver(handleIntersection);
  }, []);

  useEffect(() => {
    let targets = document.querySelectorAll('[id^="branch"]');
    targets?.forEach((target: any) => {
      observer?.current?.observe(target);
    });
  });

  const { businessBranchesDataMap } = useSelector((state: any) => ({
    businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
  }));
  const dispatch = useDispatch();

  const updateMoreOptionsMenuData = (updatedValues: {
    selectedOptionId?: string;
    outletData?: any;
    anchorEl?: null | HTMLElement;
  }) => {
    setMoreOptionsMenuData((prevState: any) => ({
      ...prevState,
      ...updatedValues,
    }));
  };

  function handleIntersection(entries: any) {
    entries.forEach((entry:any) => {
      if (entry.isIntersecting) {
        let branchId = entry.target?.id?.split('-')[1];
        fetchOutletStatusUsingBranch(branchId);
      }
    });
  }

  function clearMoreOptionsMenuData() {
    setMoreOptionsMenuData({
      selectedOptionId: "",
      outletData: {},
      anchorEl: null,
    });
  }

  const handleMoreOptionsMenuClose = (e: any = null) => {
    e?.stopPropagation();
    updateMoreOptionsMenuData({ anchorEl: null });
  };

  function fetchOutletStatusUsingBranch(branchId: string) {
    if (checkBranchData(branchId)) return;
    dispatch(
      fetchCommonData({
        method: METHODS.GET,
        url: ENDPOINT.OUTLET_STATUS.getOutletStatusUsingBranch(branchId),
        callback: (res:any)=> saveOutletStatusForBranch(res, branchId),
      })
    );
  }
  /* 
    get data from localstorage and 
    return true if it is not older than COOKIE_DURATION
  */
  function checkBranchData(branchId: string) {
    let outletData = getLocalStorage(BRANCH_OUTLET_STATUS),
      cookieData = JSON.parse(outletData || "{}")[branchId];
    if (!outletData || !cookieData) return false;
    if (Object.keys(cookieData).length) {
      let { saved, time } = cookieData;
      let timeOld = moment(time);
      let tminus15 = moment().subtract(COOKIE_DURATION, "m");
      return saved && (timeOld > tminus15);
    }
    return false;
  }

/**
 * This function is callback for fetchOutletStatusUsingBranch ,
 * receive data in {...outletid : status } format and save to reducer.
 * Then set cookie data for this branchId
 * @param res @param branchId
 */
  function saveOutletStatusForBranch(res: any, branchId: string) {
    if (res.code) return;
    let outletsData = {};
    Object.entries(res).forEach((val: any) => {
      let status = val[1];
      if (status === OUTLET_STATUSES.INACTIVE) status = OUTLET_STATUSES.OFFLINE;
      else if (status === OUTLET_STATUSES.ACTIVE)
        status = OUTLET_STATUSES.ONLINE;
      outletsData = Object.assign(outletsData, {
        [val[0]]: { currentStatus: status, lastChecked: new Date() },
      });
    });
    dispatch(saveAllOutletsStatus(outletsData));
    setCookieData(branchId);
  }

  function setCookieData(branchId: string) {
    let lsData = getLocalStorage(BRANCH_OUTLET_STATUS) || "";
    let outletData = {};
    if (lsData) outletData = JSON.parse(lsData);
    let obj = { saved: true, time: moment() };
    let newOutletData = { ...outletData, [branchId]: obj };
    setLocalStorage(BRANCH_OUTLET_STATUS, JSON.stringify(newOutletData));
  }

  function fetchAllOutletsData() {
    const dataToPost = {
      aggregatorType: "Order",
      branches: "all",
      // isNew: true,
    };
    dispatch(getOutletsData({ dataToPost }));
  }

  const syncOutletMenu = () => {
    let dataToPost = {
      brid: menuSyncModalData?.outletData?.brid,
      channelDefId: menuSyncModalData?.outletData?.channelDefId,
      id: menuSyncModalData?.outletData?.id,
      type: menuSyncModalData?.outletData?.type,
    };
    const callback = (success: boolean) => {
      fetchAllOutletsData();
      if (success) {
        setApiStateModalData({
          show: true,
          modalText: "Menu Sync Successful.",
        });
        setMenuSyncModalData({ show: false });
      }
    };
    dispatch(syncSingleOutletMenu({ dataToPost, callback }));
  };

  const onBranchCheckboxClick = (branchId: string) => {
    // selected outlets for this branch
    let selectedOutletsMap =
        get(bulkActionConfig, `selectedOutletsMap.${branchId}`) || {},
      selectedOutletsList = Object.values(selectedOutletsMap) || [],
      searchSelectedOutletList = getSearchResults(
        searchData?.value,
        selectedOutletsList
      ),
      allOutletsSelected =
        searchSelectedOutletList?.length ===
        outletGroupByBranchMap[branchId]?.length;
    let dataToUpdate = bulkActionConfig?.selectedOutletsMap
      ? cloneDeep(bulkActionConfig?.selectedOutletsMap)
      : {};
    if (allOutletsSelected) {
      unset(dataToUpdate, branchId);
      setBulkActionConfig((prevVal: any) => ({
        ...prevVal,
        selectedOutletsMap: dataToUpdate,
      }));
      return;
    }
    dataToUpdate = addAllOutletsForBranch(branchId, dataToUpdate);
    setBulkActionConfig((prevVal: any) => ({
      ...prevVal,
      selectedOutletsMap: dataToUpdate,
    }));
  };

  function renderOutletList(outletList: any) {
    return (
      <>
        {outletList.map((outletData: any) => {
          return (
            <OutletListItem
              key={`outlet-item-${outletData?.id}`}
              outletData={outletData}
              setActivityLogModalData={setActivityLogModalData}
              setOutletToggleModalData={setOutletToggleModalData}
              setMenuSyncModalData={setMenuSyncModalData}
              bulkActionConfig={bulkActionConfig}
              setBulkActionConfig={setBulkActionConfig}
              updateMoreOptionsMenuData={updateMoreOptionsMenuData}
              isMoreOptionsMenuOpen={Boolean(
                moreOptionsMenuData?.anchorEl || null
              )}
              updatedOutletsConfig={updatedOutletsConfig}
              statusLoading={statusLoading}
            />
          );
        })}
      </>
    );
  }

  function renderOutletBranches(branchOutletsMap: any) {
    return (
      <>
        {!!branchOutletsMap && !!Object.keys(branchOutletsMap)?.length
          ? Object.keys(branchOutletsMap).map((branchId: string) => {
              const outletsList = branchOutletsMap[branchId] || [];
              // selected outlets for this branch
              let selectedOutletsMap =
                  get(bulkActionConfig, `selectedOutletsMap.${branchId}`) || {},
                selectedOutletsList = Object.values(selectedOutletsMap) || [],
                searchSelectedOutletList = getSearchResults(
                  searchData?.value,
                  selectedOutletsList
                ), // selected outlets visible in search results
                allOutletsSelected =
                  searchSelectedOutletList?.length ===
                  branchOutletsMap[branchId]?.length;
              return outletsList?.length ? (
                <div
                  className="branch-container px-[24px] sm:px-[16px]"
                  key={`branch-${branchId}`}
                  id={`branch-${branchId}`}
                >
                  <div
                    className={`flex items-center w-fit mb-[20px] ${
                      bulkActionConfig?.active ? "cursor-pointer" : ""
                    }`}
                    onClick={() =>
                      bulkActionConfig?.active &&
                      onBranchCheckboxClick(branchId)
                    }
                  >
                    {bulkActionConfig?.active ? (
                      <Checkbox
                        checked={allOutletsSelected}
                        className="mr-[10px]"
                      />
                    ) : null}
                    <h1 className="branch-name text-20 font-medium sm:text-[16px]">
                      {businessBranchesDataMap[branchId]?.branchName ||
                        "No Branch Name"}
                    </h1>
                  </div>
                  {renderOutletList(outletsList)}
                </div>
              ) : null;
            })
          : null}
      </>
    );
  }

  function renderSearchResults(): JSX.Element {
    let resultsCount = getBranchOutletsCount(outletGroupByBranchMap);
    return resultsCount > 0 ? (
      <div className="search-results-container">
        <div className="text-14 font-normal pb-16 opacity-50 border-b-[1px] border-[#ECECEC] mb-[20px] px-[24px] sm:px-[16px]">
          {resultsCount} results found
        </div>
        {renderOutletBranches(outletGroupByBranchMap)}
      </div>
    ) : (
      <div className="flex flex-col justify-center items-center w-full px-[24px] mt-56">
        <div className="flex items-center justify-center w-[72px] h-[72px] bg-opacity-5 bg-[#000] rounded-[8px]">
          <div className="rista-sprite ic-not-found"></div>
        </div>
        <p className="text-20 font-semibold mt-24 mb-6 opacity-40">
          No results found
        </p>
        <p className="text-13 font-normal mb-6 opacity-40 mt-[0px]">
          Please try using other name or keyword
        </p>
      </div>
    );
  }

  function renderOutletMenuSyncModal() {
    return (
      <Modal open={true} onClose={() => setMenuSyncModalData({ show: false })}>
        <Box
          className={`outlet-menu-sync-modal absolute bg-white -translate-x-1/2 -translate-y-1/2
            left-1/2 top-1/2 pt-[10px] pb-[20px] px-[24px] rounded-[8px] border-[1px] border-[#f0f0f0]
            shadow-[0px_2px_10px_#00000014]
            sm:left-0 sm:right-0 sm:bottom-0 sm:top-auto sm:translate-x-0 sm:translate-y-0
            sm:w-full sm:rounded-b-[0] sm:px-[16px]
          `}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-18 font-semibold">Sync Menu</h2>
            <div
              className="rista-sprite ic-cross-black"
              onClick={() => setMenuSyncModalData({ show: false })}
            ></div>
          </div>
          <div className="text-16 text-black/75 font-normal flex items-center mt-[12px]">
            <p className="min-w-[72px]">Branch:</p>
            <p className="">
              {businessBranchesDataMap[menuSyncModalData?.outletData?.brid]
                ?.branchName || "-"}
            </p>
          </div>
          <div className="text-16 text-black/75 font-normal flex items-center mt-[6px] mb-[40px]">
            <p className="min-w-[72px]">Channel:</p>
            <p className="">
              {menuSyncModalData?.outletData?.channelDefName || "-"}
            </p>
          </div>
          <div className="flex justify-end">
            <CtaComponent
              ctaSize={CTA_SIZE.medium}
              ctaText={"I’ll Do Later"}
              ctaType={CTA_TYPE.secondary_cta}
              onCtaClick={() => setMenuSyncModalData({ show: false })}
              customClass={"mr-[6px] sm:w-[calc(50%-6px)] text-14"}
            ></CtaComponent>
            <CtaComponent
              ctaSize={CTA_SIZE.medium}
              ctaText={"Confirm"}
              ctaType={CTA_TYPE.active_cta}
              onCtaClick={syncOutletMenu}
              customClass={"ml-[6px] sm:w-[calc(50%-6px)] text-14"}
            ></CtaComponent>
          </div>
        </Box>
      </Modal>
    );
  }

  return selectedBranches?.length ? (
    <>
      {searchData.value.length
        ? renderSearchResults()
        : renderOutletBranches(outletGroupByBranchMap)}
      {activityLogModalData?.show ? (
        <ActivityLogModal
          modalData={activityLogModalData}
          onModalClose={() => setActivityLogModalData({ show: false })}
        />
      ) : null}
      {outletToggleModalData?.show ? (
        <OutletStatusToggleModal
          outletData={outletToggleModalData?.outletData}
          onModalClose={() => setOutletToggleModalData({ show: false })}
          setApiStateModalData={setApiStateModalData}
        />
      ) : null}
      {menuSyncModalData?.show ? renderOutletMenuSyncModal() : null}
      {!!moreOptionsMenuData?.anchorEl ? (
        <OutletOptionsMenu
          handleMenuClose={handleMoreOptionsMenuClose}
          updateMoreOptionsMenuData={updateMoreOptionsMenuData}
          moreOptionsMenuData={moreOptionsMenuData}
        />
      ) : null}

      {moreOptionsMenuData?.selectedOptionId ? (
        <RenderSelectedOption
          moreOptionsMenuData={moreOptionsMenuData}
          updateMoreOptionsMenuData={updateMoreOptionsMenuData}
          clearMoreOptionsMenuData={clearMoreOptionsMenuData}
          setApiStateModalData={setApiStateModalData}
        />
      ) : null}
    </>
  ) : (
    <div className="flex flex-col justify-center items-center w-full p-24 text-center absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
      <img src={emptyListPlaceholder} alt="" />
      <h1 className="mt-[14px] text-[#4B4B4B] text-20 mb-[16px] font-bold">
        Feels So Empty !
      </h1>
      <p className="text-[15px] text-black/70 mb-[28px]">
        You haven’t selected any branches.
      </p>
    </div>
  );
};

export default OutletViewComponent;
